import authService from '@/authentication/authentication.service';

export class BaseApiClient {
  public async send<T = void>(
    method: HttpVerb,
    url: string,
    data?: any,
    optionOverrides?: Partial<ApiOptions>,
  ): Promise<T> {
    const headers: HeadersInit = {};

    const defaultOptions: ApiOptions = { responseType: undefined, useAuth: true };
    const options: ApiOptions = { ...defaultOptions, ...optionOverrides };

    if (options.useAuth) {
      const token = await authService.getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const fetchOptions: RequestInit = { method, headers };

    if (method !== 'GET' && method !== 'HEAD') {
      if (data instanceof FormData) {
        fetchOptions.body = data;
      } else {
        headers['Content-Type'] = 'application/json';
        fetchOptions.body = JSON.stringify(data);
      }
    }

    try {
      const response = await fetch(url, fetchOptions);
      if (!response.ok) {
        return this.handleError(response);
      }

      const contentLength = response.headers.get('Content-Length');
      const isBlob =
        (options.responseType ?? this.inferResponseType(response.headers.get('Content-Type')?.toLowerCase() || '')) ===
        'blob';
      if (!isBlob && (contentLength === '0' || !response.body)) {
        return undefined as T;
      }

      const contentType = response.headers.get('Content-Type')?.toLowerCase() || '';
      const effectiveResponseType = options.responseType || this.inferResponseType(contentType);

      switch (effectiveResponseType) {
        case 'json': {
          const text = await response.text();
          return text ? (JSON.parse(text) as T) : (undefined as T);
        }
        case 'blob': {
          const data = await response.blob();
          const cd = response.headers.get('Content-Disposition');
          const fileName = this.getFilenameFromHeader(cd) || this.fallbackNameFromUrl(url);
          return { data, fileName } as T;
        }
        case 'arraybuffer':
          return (await response.arrayBuffer()) as T;
        case 'text':
        default:
          return (await response.text()) as T;
      }
    } catch (err) {
      return Promise.reject({
        status: -1,
        message: (err as Error).message,
      } as ApiError);
    }
  }

  private inferResponseType(contentType: string): 'text' | 'json' | 'blob' | 'arraybuffer' {
    if (contentType.includes('application/json')) return 'json';
    if (contentType.includes('text/')) return 'text';
    if (contentType.includes('application/octet-stream') || contentType.includes('application/pdf')) return 'blob';
    if (contentType.includes('arraybuffer')) return 'arraybuffer';
    return 'text';
  }

  private getFilenameFromHeader(header: string | null): string {
    if (!header) return '';

    // RFC 5987: filename*=UTF-8''encodedName
    const starMatch = header.match(/filename\*\s*=\s*([^;]+)/i);
    if (starMatch) {
      const value = starMatch[1].trim();
      const parts = value.split("''");
      const encoded = parts.length === 2 ? parts[1] : value;
      try {
        return decodeURIComponent(encoded.replace(/^"(.*)"$/, '$1'));
      } catch {
        // fall through
      }
    }

    const quotedMatch = header.match(/filename\s*=\s*"([^"]+)"/i);
    if (quotedMatch) return quotedMatch[1];

    const unquotedMatch = header.match(/filename\s*=\s*([^;]+)/i);
    if (unquotedMatch) return unquotedMatch[1].trim().replace(/^"(.*)"$/, '$1');

    return '';
  }

  private fallbackNameFromUrl(url: string): string {
    try {
      const u = new URL(url, window.location.origin);
      const lastSegment = u.pathname.split('/').filter(Boolean).pop();
      return lastSegment || 'download';
    } catch {
      return 'download';
    }
  }

  private async handleError(response: Response): Promise<never> {
    const message = await response.text();
    return Promise.reject({
      status: response.status,
      message: message || `Request failed with status ${response.statusText}`,
    } as ApiError);
  }
}

export const api = new BaseApiClient();

export interface BlobContent {
  data: Blob;
  fileName: string;
}

export interface ApiError {
  status: number;
  message: string;
}

export interface ApiOptions {
  responseType?: 'text' | 'json' | 'blob' | 'arraybuffer';
  useAuth: boolean;
}

export type HttpVerb = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD';
